import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPlatformFinancials } from "../../api/adminApi";
import {
  Box, Card, CardContent, Typography, Select, MenuItem,
  TextField, Button, CircularProgress, Stack, Grid, IconButton
} from "@mui/material";

import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// 👇 Imported your ScrollToTop utility
import ScrollToTop from "../../utils/ScrollToTop";

export default function ZonalAdminFinance({ zonalAdminId }) {
  const navigate = useNavigate();
  const { id: urlZonalAdminId } = useParams();
  const activeAdminId = zonalAdminId || urlZonalAdminId;

  const [filter, setFilter] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (filter === "custom" && (!startDate || !endDate)) return;
    try {
      setLoading(true);
      const params = { filter };
      if (filter === "custom") { params.start_date = startDate; params.end_date = endDate; }

      if (activeAdminId) {
        params.zonal_admin_id = activeAdminId;
      }

      const res = await getPlatformFinancials(params);
      setSummary(res.data);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || "Failed to load platform financials");
    } finally {
      setLoading(false);
    }
  }, [filter, startDate, endDate, activeAdminId]);

  useEffect(() => {
    if (filter !== "custom") fetchSummary();
  }, [filter, fetchSummary]);

  const handleExportExcel = () => {
    if (!summary || !summary.orders_export || summary.orders_export.length === 0) return;

    let csvContent = "Order ID,Date,Payment Mode,Total Paid (Rs),Items Ordered,Total Vendor Price,Coupon Amount,Mealno Profit\n";

    let sumTotalPaid = 0;
    let sumVendorPrice = 0;
    let sumCoupon = 0;
    let sumProfit = 0;

    summary.orders_export.forEach(o => {
      sumTotalPaid += (o.amount || 0);
      sumVendorPrice += (o.vendor_price || 0);
      sumCoupon += (o.coupon_amount || 0);
      sumProfit += (o.mealno_profit || 0);

      csvContent += `${o.order_id},"${o.date}","${o.payment_mode || 'N/A'}",${o.amount},"${o.items}",${o.vendor_price},${o.coupon_amount},${o.mealno_profit}\n`;
    });

    csvContent += "\n".repeat(10);

    csvContent += `Total Payemnt =,,,${sumTotalPaid.toFixed(2)}\n`;
    csvContent += `Total Vendor Payment =,,,,,${sumVendorPrice.toFixed(2)}\n`;
    csvContent += `Total Coupon cost =,,,,,,${sumCoupon.toFixed(2)}\n`;
    csvContent += `Total Mealno Profit =,,,,,,,${sumProfit.toFixed(2)}\n`;
    csvContent += `Net Settlement =,,,,,,,${summary.settlement.action} ${summary.settlement.amount.toFixed(2)}\n`;

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `Mealno_Finance_${filter}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    if (!summary || !summary.orders_export || summary.orders_export.length === 0) return;

    const doc = new jsPDF();
    doc.text(`Mealno Platform Financials (${filter.toUpperCase()})`, 14, 15);

    const tableColumn = ["ID", "Date", "Mode", "Total Paid", "Items", "Vendor P.", "Coupon", "Profit"];
    const tableRows = [];

    let sumTotalPaid = 0;
    let sumVendorPrice = 0;
    let sumCoupon = 0;
    let sumProfit = 0;

    summary.orders_export.forEach(o => {
      sumTotalPaid += (o.amount || 0);
      sumVendorPrice += (o.vendor_price || 0);
      sumCoupon += (o.coupon_amount || 0);
      sumProfit += (o.mealno_profit || 0);

      const rowData = [
        o.order_id,
        o.date,
        o.payment_mode || "N/A",
        `Rs ${o.amount}`,
        o.items,
        `Rs ${o.vendor_price}`,
        `Rs ${o.coupon_amount}`,
        `Rs ${o.mealno_profit}`
      ];
      tableRows.push(rowData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 7 },
      columnStyles: { 4: { cellWidth: 50 } }
    });

    const finalY = doc.lastAutoTable.finalY + 10;

    doc.setFontSize(10);
    doc.text(`Total Payemnt = Rs ${sumTotalPaid.toFixed(2)}`, 14, finalY);
    doc.text(`Total Vendor Payment = Rs ${sumVendorPrice.toFixed(2)}`, 14, finalY + 6);
    doc.text(`Total Coupon cost = Rs ${sumCoupon.toFixed(2)}`, 14, finalY + 12);
    doc.text(`Total Mealno Profit = Rs ${sumProfit.toFixed(2)}`, 14, finalY + 18);

    doc.setFont("helvetica", "bold");
    doc.text(`Net Settlement = ${summary.settlement.action} Rs ${summary.settlement.amount.toFixed(2)}`, 14, finalY + 28);

    doc.save(`Mealno_Finance_${filter}.pdf`);
  };

  const statBoxStyle = {
    p: 3, borderRadius: '20px', textAlign: 'center',
    boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
    border: '1px solid rgba(0,0,0,0.05)',
    transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-3px)' }
  };

  const hasData = summary && summary.total_orders > 0;
  const pieData = hasData ? [
    { name: "Vendor Payouts", value: summary.total_vendor_revenue || 0 },
    { name: "Mealno Profit", value: summary.total_mealno_profit || 0 }
  ].filter(item => item.value > 0)
    : [
      { name: "No Orders", value: 1 }
    ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: "auto", minHeight: "100vh", bgcolor: "#FFFDF8", mb: 10 }}>
      <ScrollToTop />

      {/* 👇 NEW: Standalone Header matching other pages */}
      <Box display="flex" alignItems="center" gap={1.5} mb={4}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: '#FFFFFF',
            border: '1px solid #ECE5DA',
            color: '#3A2C23',
            '&:hover': { bgcolor: '#F8C332', borderColor: '#F8C332' }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
          Zonal Admin Financials
        </Typography>
      </Box>

      {/* 🎛️ FILTER CONTROLS */}
      <Card elevation={0} sx={{ borderRadius: '24px', border: '1px solid #ECE5DA', mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <AccountBalanceWalletIcon sx={{ color: '#F8C332', fontSize: 28 }} />
              <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23' }}>
                Overview
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ borderRadius: '12px', minWidth: '150px', bgcolor: '#FFFDF8', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ECE5DA' } }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>

              {filter === "custom" && (
                <>
                  <TextField type="date" label="Start" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                  <TextField type="date" label="End" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                  <Button variant="contained" onClick={fetchSummary} disabled={!startDate || !endDate || loading} sx={{ borderRadius: '12px', py: 1.5, bgcolor: '#3A2C23', color: 'white', fontWeight: 'bold' }}>
                    Search
                  </Button>
                </>
              )}

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPDF}
                  disabled={!summary || summary.total_orders === 0}
                  sx={{ borderRadius: '12px', py: 1.5, px: 2, bgcolor: '#DC2626', color: 'white', fontWeight: '900', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)', '&:hover': { bgcolor: '#B91C1C' } }}
                >
                  PDF
                </Button>

                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportExcel}
                  disabled={!summary || summary.total_orders === 0}
                  sx={{ borderRadius: '12px', py: 1.5, px: 2, bgcolor: '#F8C332', color: '#3A2C23', fontWeight: '900', boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' } }}
                >
                  EXCEL
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* 📊 METRICS & GRAPH */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#F8C332' }} /></Box>
      ) : summary ? (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}>
              <Box sx={{ ...statBoxStyle, bgcolor: 'rgba(58, 44, 35, 0.02)' }}>
                <Typography variant="body2" fontWeight="800" sx={{ color: '#A59D96', mb: 1 }}>TOTAL ORDERS</Typography>
                <Typography variant="h4" fontWeight="900" sx={{ color: '#3A2C23' }}>{summary.total_orders}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ ...statBoxStyle, bgcolor: 'rgba(52, 168, 83, 0.05)' }}>
                <Typography variant="body2" fontWeight="800" sx={{ color: '#34A853', mb: 1 }}>ITEMS SOLD</Typography>
                <Typography variant="h4" fontWeight="900" sx={{ color: '#2E7D32' }}>{summary.items_sold}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ ...statBoxStyle, bgcolor: 'rgba(232, 93, 63, 0.05)' }}>
                <Typography variant="body2" fontWeight="800" sx={{ color: '#E85D3F', mb: 1 }}>VENDOR PAYOUTS</Typography>
                <Typography variant="h4" fontWeight="900" sx={{ color: '#C0392B' }}>₹{summary.total_vendor_revenue}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ ...statBoxStyle, bgcolor: 'rgba(248, 195, 50, 0.15)', border: '1px solid rgba(248, 195, 50, 0.4)' }}>
                <Typography variant="body2" fontWeight="900" sx={{ color: '#D97706', mb: 1 }}>MEALNO PROFIT</Typography>
                <Typography variant="h4" fontWeight="900" sx={{ color: '#B45309' }}>₹{summary.total_mealno_profit}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Box sx={{ ...statBoxStyle, bgcolor: 'rgba(52, 168, 83, 0.05)', borderColor: 'rgba(52, 168, 83, 0.15)', width: '100%', height: '100%', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#2E7D32', mb: 0.5 }}>TOTAL AMOUNT HANDLED</Typography>
                <Typography variant="h3" fontWeight="900" sx={{ color: '#1B5E20' }}>₹{summary.total_app_revenue}</Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3, width: '100%' }}>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: '12px', bgcolor: 'rgba(52, 168, 83, 0.1)' }}>
                    <Typography variant="caption" fontWeight="800" sx={{ color: '#2E7D32', display: 'block', mb: 0.5 }}>ONLINE / UPI</Typography>
                    <Typography variant="h6" fontWeight="900" sx={{ color: '#1B5E20' }}>₹{summary.online_amount || 0}</Typography>
                  </Box>

                  <Box sx={{ flex: 1, p: 1.5, borderRadius: '12px', bgcolor: 'rgba(66, 133, 244, 0.08)' }}>
                    <Typography variant="caption" fontWeight="800" sx={{ color: '#1565C0', display: 'block', mb: 0.5 }}>OFFLINE / CASH</Typography>
                    <Typography variant="h6" fontWeight="900" sx={{ color: '#0D47A1' }}>₹{summary.offline_amount || 0}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>

            {summary.settlement && (
              <Grid item xs={12} md={5}>
                <Box sx={{
                  ...statBoxStyle,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  bgcolor: summary.settlement.action === 'PAY' ? 'rgba(232, 93, 63, 0.05)' : summary.settlement.action === 'RECEIVE' ? 'rgba(52, 168, 83, 0.05)' : 'rgba(0,0,0,0.02)',
                  borderColor: summary.settlement.action === 'PAY' ? 'rgba(232, 93, 63, 0.2)' : summary.settlement.action === 'RECEIVE' ? 'rgba(52, 168, 83, 0.2)' : 'rgba(0,0,0,0.05)'
                }}>
                  <Typography variant="subtitle2" fontWeight="800" sx={{ color: summary.settlement.action === 'PAY' ? '#C0392B' : summary.settlement.action === 'RECEIVE' ? '#1B5E20' : '#666', mb: 0.5 }}>
                    NET SETTLEMENT
                  </Typography>
                  <Typography variant="h3" fontWeight="900" sx={{ color: summary.settlement.action === 'PAY' ? '#C0392B' : summary.settlement.action === 'RECEIVE' ? '#1B5E20' : '#3A2C23' }}>
                    ₹{summary.settlement.amount}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="800" sx={{ color: summary.settlement.action === 'PAY' ? '#E85D3F' : summary.settlement.action === 'RECEIVE' ? '#2E7D32' : '#A59D96', mt: 1 }}>
                    {summary.settlement.message}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          <Card elevation={0} sx={{ borderRadius: '24px', border: "1px solid #ECE5DA", bgcolor: '#FFFFFF', p: { xs: 1, sm: 3 } }}>
            <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23', mb: 3, ml: 2, textAlign: 'center' }}>
              Revenue Split
            </Typography>
            <Box sx={{ width: '100%', height: 320, minHeight: 320, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <ResponsiveContainer width="99%" height={300}>
                <PieChart>
                  <defs>
                    <linearGradient id="platformVendorGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#E85D3F" />
                      <stop offset="100%" stopColor="#C0392B" />
                    </linearGradient>
                    <linearGradient id="platformProfitGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#F8C332" />
                      <stop offset="100%" stopColor="#D97706" />
                    </linearGradient>
                  </defs>

                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={120}
                    paddingAngle={6}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => {
                      let fillColor = "#ECE5DA";
                      if (hasData) {
                        fillColor = entry.name === "Vendor Payouts" ? "url(#platformVendorGrad)" : "url(#platformProfitGrad)";
                      }
                      return <Cell key={`cell-${index}`} fill={fillColor} />;
                    })}
                  </Pie>

                  {hasData && (
                    <Tooltip
                      formatter={(value) => `₹${value}`}
                      contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 32px rgba(0,0,0,0.1)' }}
                      itemStyle={{ fontWeight: 800, color: '#3A2C23' }}
                    />
                  )}
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="circle"
                    wrapperStyle={{ fontWeight: 800, color: '#6E655E', paddingTop: '20px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </>
      ) : null}
    </Box>
  );
}